'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Grid3X3, Circle, Image, Shapes } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import { MazeMode } from './types/maze-types';

interface MazeModeSelectorProps {
  mode: MazeMode;
  onModeChange: (mode: MazeMode) => void;
  className?: string;
}

const MODES: { value: MazeMode; testId: string; labelKey: string; Icon: typeof Grid3X3 }[] = [
  { value: 'rectangular', testId: 'maze-mode-rect', labelKey: 'games.maze.modeRect', Icon: Grid3X3 },
  { value: 'radial', testId: 'maze-mode-radial', labelKey: 'games.maze.modeRadial', Icon: Circle },
  { value: 'image', testId: 'maze-mode-image', labelKey: 'games.maze.modeImage', Icon: Image },
  { value: 'template', testId: 'maze-mode-template', labelKey: 'games.maze.modeTemplate', Icon: Shapes },
];

export function MazeModeSelector({ mode, onModeChange, className }: MazeModeSelectorProps) {
  const t = useTranslations();

  return (
    <div
      className={cn('flex flex-wrap gap-1 justify-center', className)}
      data-testid="maze-mode-selector"
      role="tablist"
      aria-label={t('games.maze.modeLabel')}
    >
      {MODES.map(({ value, testId, labelKey, Icon }) => {
        const isActive = mode === value;
        return (
          <Button
            key={value}
            type="button"
            role="tab"
            aria-selected={isActive}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => onModeChange(value)}
            className={cn(
              'gap-1.5 text-xs px-3 py-1 transition-all',
              isActive
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'hover:bg-primary/10'
            )}
            data-testid={testId}
            data-selected={isActive}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="hidden min-[400px]:inline">{t(labelKey)}</span>
          </Button>
        );
      })}
    </div>
  );
}
